import { useEffect, useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Organization {
  id: string;
  name: string;
  slug: string;
  defaultMarginPercent: string;
  baseCurrency: string;
}

export default function OrganizationSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const [name, setName] = useState("");
  const [defaultMarginPercent, setDefaultMarginPercent] = useState("");
  const [baseCurrency, setBaseCurrency] = useState("");

  const { data: org, isLoading } = useQuery<Organization>({
    queryKey: ["/api/me/organization"],
    queryFn: () => api.get<Organization>(`/me/organization`, sessionId),
  });

  useEffect(() => {
    if (org) {
      setName(org.name || "");
      setDefaultMarginPercent(org.defaultMarginPercent || "");
      setBaseCurrency(org.baseCurrency || "USD");
    }
  }, [org]);

  const saveMutation = useMutation({
    mutationFn: (payload: { name: string; defaultMarginPercent: string; baseCurrency: string }) =>
      api.patch<Organization>(`/me/organization`, payload, sessionId),
    onSuccess: () => toast({ title: "Organization updated" }),
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organization"
        description="Configure organization-wide settings"
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5" /> Organization Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Organization Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="input-org-name" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Slug</label>
            <Input value={org?.slug || ""} disabled />
            <p className="text-xs text-muted-foreground mt-1">Slug cannot be changed</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Default Margin %</label>
              <Input
                type="number"
                step="0.01"
                value={defaultMarginPercent}
                onChange={(e) => setDefaultMarginPercent(e.target.value)}
                data-testid="input-default-margin"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Base Currency</label>
              <Input
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value.toUpperCase())}
                maxLength={3}
                data-testid="input-base-currency"
              />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => saveMutation.mutate({ name, defaultMarginPercent, baseCurrency })}
              disabled={saveMutation.isPending}
              data-testid="button-save-org"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
